/**
 * GTM-Analyst — Bewertungslogik, portiert aus dem Cockpit-PoC (cockpit/index.html).
 * Reine Logik ohne DOM: gibt strukturierte Daten zurueck, die die React-Seite rendert.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { GtmReport } from '../../../../shared/gtmTypes.js';

export interface Row {
  idx: number;
  tool: string;
  convId: string;
  kennName: string;
  label: string | null;
  ga4Event: string | null;
  event: string | null;
  method: string;
  kontext: string[];
  result: RowResult | null;
  resolvedFromRequest?: string | null;
  _running?: boolean;
}

export interface Group {
  event: string;
  rows: Row[];
  savedAs?: string | null;
}

export type ResultTone = 'ok' | 'warn' | 'bad';
export interface RowResult {
  tone: ResultTone;
  label: string;
  details: string[];
}

const CONVERSION_TOOLS = /Google Ads|Analytics 4|UET|Meta|LinkedIn|TikTok/;

export function rowsFromReport(report: GtmReport): Row[] {
  const rows: Row[] = [];
  for (const t of report.tags) {
    if (t.paused || !t.simulation) continue;
    if (!CONVERSION_TOOLS.test(t.tool)) continue;
    if (/Config|Linker|Remarketing|Tracking Code/.test(t.type) && !t.ids.conversionLabel) continue;
    const convId = t.ids.conversionId || t.ids.measurementId || t.ids.pixelId || t.ids.tagId || t.ids.partnerId || '—';
    const kennName = t.ids.conversionLabel || t.ids.customEventName || t.ids.eventName || '—';
    rows.push({
      idx: t.index, tool: t.tool, convId, kennName,
      label: t.ids.conversionLabel || null,
      ga4Event: t.tool.includes('Analytics') ? (t.ids.eventName || null) : null,
      event: t.simulation.event, method: t.simulation.best,
      kontext: t.simulation.kontext || [], result: null,
    });
  }
  return rows;
}

export function buildGroups(rows: Row[]): Group[] {
  const testable = rows.filter((r) => r.method === 'dataLayer_push');
  const byEvent: Record<string, Row[]> = {};
  for (const r of testable) (byEvent[r.event as string] = byEvent[r.event as string] || []).push(r);
  return Object.entries(byEvent)
    .sort((a, b) => Number(a[0] === 'cookie_consent_update') - Number(b[0] === 'cookie_consent_update') || a[0].localeCompare(b[0]))
    .map(([event, grpRows]) => ({
      event,
      // Zeilen ohne Zusatzbedingungen zuerst (muessen feuern), bedingte darunter
      rows: grpRows.slice().sort((a, b) => Number(a.kontext.length > 0) - Number(b.kontext.length > 0)),
    }));
}

// eCommerce-Testdaten je Event (GA4: transaction_id/value/currency/items · Meta: value/currency)
export function buildEventPayload(eventName: string | null): any {
  const n = String(eventName || '').toLowerCase();
  const items = [{ item_id: 'TEST-SKU-1', item_name: 'ambix Scanner Testprodukt', price: 1.0, quantity: 1 }];
  if (/purchase|kauf|order|transaction/.test(n)) {
    return { ecommerce: { transaction_id: 'TEST-AMBIX-' + Date.now(), value: 1.0, currency: 'EUR', tax: 0, shipping: 0, items } };
  }
  if (/add_to_cart|begin_checkout|view_item|checkout|warenkorb/.test(n)) {
    return { ecommerce: { value: 1.0, currency: 'EUR', items } };
  }
  return null;
}

// Kundentyp-Heuristik: Lead vs. eCommerce anhand der Container-Events
const ECOM_PATTERNS = /purchase|add_to_cart|begin_checkout|view_item|remove_from_cart|checkout|transaction|ecommerce|order|warenkorb|kauf/i;
const LEAD_PATTERNS = /form_sent|form_submit|contact|kontakt|lead|meeting|termin|anfrage|newsletter|signup|sign_up|demo|bewerbung|potenzialanalyse|cform|whitepaper|casestudy|download|optin/i;

export interface CustomerType { typ: string; farbe: string; ecom: string[]; lead: string[]; }
export function classifyCustomerType(report: GtmReport): CustomerType {
  const names = new Set<string>();
  for (const t of report.tags) {
    if (t.paused) continue;
    for (const v of [t.ids?.eventName, t.ids?.customEventName, t.simulation?.event]) {
      if (v && !String(v).startsWith('gtm.') && !String(v).startsWith('{{')) names.add(String(v));
    }
  }
  const ecom = [...names].filter((n) => ECOM_PATTERNS.test(n));
  const lead = [...names].filter((n) => LEAD_PATTERNS.test(n));
  let typ: string; let farbe: string;
  if (ecom.length && lead.length) { typ = 'Hybrid (eCommerce + Lead)'; farbe = '#7856FF'; }
  else if (ecom.length >= 2) { typ = 'eCommerce'; farbe = '#E37400'; }
  else if (lead.length >= 2) { typ = 'Lead-Gen'; farbe = '#22c55e'; }
  else { typ = 'unklar (zu wenige Signale)'; farbe = '#8b98a5'; }
  return { typ, farbe, ecom, lead };
}

export interface Diagnosis {
  valid: boolean;
  tone: ResultTone;
  text: string;
  ec?: any;
  dlText?: string;
}
export function diagnose(run: any): Diagnosis {
  const acc = run?.consent_accepted;
  const ec = acc?.eventCheck?.events?.[0];
  const consentClicked = acc?.screening?.cmp_button_clicked === true;
  const errors = acc?.errors || [];
  if (!acc) {
    const keys = run && typeof run === 'object' ? Object.keys(run).join(', ') : String(run);
    return { valid: false, tone: 'bad', text: 'Scan-Problem: keine consent_accepted-Daten · Response enthielt: ' + (keys || 'nichts') };
  }
  if (errors.length) return { valid: false, tone: 'bad', text: 'Scan-Problem: ' + String(errors[0]).slice(0, 120) };
  if (!consentClicked) return { valid: false, tone: 'bad', text: 'Scan-Problem: Consent-Klick fehlgeschlagen → Tags konnten nie laden. Bitte erneut testen.' };
  if (!ec || ec.eventName == null) return { valid: false, tone: 'bad', text: 'Scan-Problem: eventCheck fehlt in der Response' };
  const dlText = JSON.stringify(acc.dataLayer_consent_accepted?.dataLayer || acc.dataLayer_consent_accepted || []);
  return { valid: true, tone: 'ok', ec, dlText, text: `Scan ok · Consent akzeptiert · ${ec.triggeredRequestCount} Request(s) im Event-Fenster beobachtet` };
}

// Prueft jede Zusatzbedingung gegen den echten Run
export function condInsights(r: Row, d: Diagnosis, pageUrl: string): string[] {
  const out: string[] = [];
  for (const k of r.kontext) {
    const dlMatch = k.match(/\{\{DL: ([^}]+)\}\}\s+\w[\w ]*\s+"([^"]*)"/);
    const urlMatch = k.match(/\{\{Page URL[^}]*\}\}\s+(\w[\w ]*)\s+"([^"]*)"/);
    if (dlMatch) {
      const [, name, expected] = dlMatch;
      const present = (d.dlText || '').includes('"' + name + '"');
      const valueHit = present && expected && (d.dlText || '').includes(String(expected));
      if (!present) out.push(`Bedingung „${name}": Variable kam im dataLayer des Runs NICHT vor → ✗ nicht erfüllt (wahrscheinliche Ursache)`);
      else if (expected && !valueHit) out.push(`Bedingung „${name}": Variable vorhanden, erwarteter Wert „${expected}" aber nicht gefunden → ✗ vermutlich nicht erfüllt`);
      else out.push(`Bedingung „${name}": im dataLayer des Runs vorhanden → ✓ vermutlich erfüllt`);
    } else if (urlMatch) {
      const [, op, expected] = urlMatch;
      const hit = op.includes('contains') ? pageUrl.includes(expected) : pageUrl === expected || pageUrl.endsWith(expected);
      out.push(`URL-Bedingung „${expected}": gescannt wurde ${pageUrl} → ${hit ? '✓ erfüllt' : '✗ nicht erfüllt · Test auf der passenden Unterseite wiederholen'}`);
    } else {
      out.push(`Bedingung nicht automatisch prüfbar: ${k}`);
    }
  }
  return out;
}

export function evaluateRow(r: Row, d: Diagnosis, pageUrl: string): RowResult {
  const ec = d.ec;
  const reqs: any[] = ec.triggeredRequests || [];
  const convs: any[] = ec.googleAds?.conversions || [];
  let hit = false; let exact = true; let checkName = '';
  // {{Event}} ist eine Pass-through-Variable → zum konkreten Event des Laufs aufloesen
  const resolvedGa4 = r.ga4Event === '{{Event}}' ? r.event : r.ga4Event;
  if (r.label) { hit = convs.some((c) => c.conversionLabel === r.label); checkName = 'Conversion-Label im Request gesucht'; }
  else if (resolvedGa4 && !resolvedGa4.startsWith('{{')) { hit = (ec.ga4Events || []).some((g: any) => g.eventName === resolvedGa4); checkName = 'GA4-Event-Name „' + resolvedGa4 + '" im Request gesucht'; }
  else {
    const toolMap: [RegExp, string, string][] = [[/UET/, 'bing', 'Microsoft/UET'], [/Meta/, 'facebook_pixel', 'Meta'], [/LinkedIn/, 'linkedin', 'LinkedIn'], [/TikTok/, 'tiktok', 'TikTok'], [/Analytics/, 'google_analytics', 'GA4']];
    const entry = toolMap.find(([re]) => re.test(r.tool));
    const kanalReqs = entry ? reqs.filter((x) => x.tool === entry[1]) : [];
    let requestEventName: string | null = null;
    for (const x of kanalReqs) {
      try {
        const u = new URL(x.url);
        for (const p of ['ev', 'en', 'ea']) {
          const v = u.searchParams.get(p);
          if (v && v.length > 1) { requestEventName = v; break; }
        }
      } catch { /* ignore */ }
      if (requestEventName) break;
    }
    if (requestEventName) r.resolvedFromRequest = requestEventName;
    const kenn = r.kennName === '{{Event}}' ? r.event : r.kennName;
    const needle = (kenn && !['—', 'custom', 'standard'].includes(kenn) && !kenn.startsWith('{{')) ? kenn : null;
    if (needle && kanalReqs.some((x) => (x.url || '').includes(needle) || (x.url || '').includes(encodeURIComponent(needle)))) {
      hit = true; exact = true;
      r.resolvedFromRequest = r.resolvedFromRequest || needle;
      checkName = 'Event-Name „' + needle + '" in der ' + (entry ? entry[2] : '') + '-Request-URL gesucht';
    } else {
      exact = false;
      hit = kanalReqs.length > 0;
      checkName = 'Requests des Kanals ' + (entry ? entry[2] : '?') + ' gezählt';
    }
  }
  if (hit && exact) return { tone: 'ok', label: 'Request ausgelöst', details: [checkName + ' → gefunden', 'Request wurde geblockt (kein Bot-Traffic)'] };
  if (hit && !exact) {
    const insights = r.kontext.length ? condInsights(r, d, pageUrl) : [];
    const condNotMet = insights.some((t) => t.includes('✗'));
    if (condNotMet) {
      return { tone: 'bad', label: 'vermutlich nicht gefeuert', details: ['Bedingung dieses Tags war im Run nicht erfüllt', 'Es gab zwar Requests dieses Kanals, die stammen aber sehr wahrscheinlich von einem anderen Tag', ...insights] };
    }
    return { tone: 'warn', label: 'wahrscheinlich ausgelöst', details: [checkName + ' → gefunden', 'Tag-genaue Kennung im Request nicht verfügbar (nur Kanal-Nachweis)', 'Beobachtete Requests wurden geblockt (kein Bot-Traffic)', ...insights] };
  }
  return { tone: 'bad', label: 'kein Request beobachtet', details: [checkName + ' → nichts gefunden', ...(r.kontext.length ? condInsights(r, d, pageUrl) : ['Keine Zusatzbedingungen · das Tag hätte reagieren müssen'])] };
}

// Ausschlussverfahren: Upgrade von ~ auf ✅, wenn nur EIN Tag des Kanals uebrig bleibt
export function upgradeByElimination(g: Group): void {
  const KANAELE: [RegExp, string][] = [[/Meta/, 'Meta'], [/UET/, 'Microsoft/UET'], [/LinkedIn/, 'LinkedIn'], [/TikTok/, 'TikTok']];
  for (const [re, name] of KANAELE) {
    const rows = g.rows.filter((r) => re.test(r.tool));
    if (rows.length === 0) continue;
    const probable = rows.filter((r) => r.result && r.result.label.includes('wahrscheinlich ausgelöst'));
    const others = rows.filter((r) => !probable.includes(r));
    const allOthersNegative = others.every((r) => r.result && ['vermutlich nicht gefeuert', 'kein Request beobachtet', 'Request ausgelöst'].some((l) => r.result!.label.includes(l)));
    if (probable.length !== 1 || !allOthersNegative) continue;
    const r = probable[0];
    const evName = r.resolvedFromRequest;
    if (!evName || evName === 'PageView') continue;
    r.result = {
      tone: 'ok', label: 'Request ausgelöst',
      details: ['Zuordnung per Ausschlussverfahren: beobachtetes Request-Event „' + evName + '"', 'Einziges in Frage kommendes ' + name + '-Tag dieser Event-Gruppe (alle anderen ausgeschlossen)', 'Request wurde geblockt (kein Bot-Traffic)'],
    };
  }
}
